import {
  InfiniteData,
  useMutation,
  useQueryClient
} from '@tanstack/react-query'

import { ListRequestReturnType } from '@/api/models/notifications/lib/notifications.types'
import { notificationHelpersByAll } from '@/api/models/notifications/utils/notificationHelpersByAll'
import { useSelectedProjectStore } from '@/components/entities/common/model/selected-project.store'
import { notificationTypeOptionsMap } from '@/components/entities/notification/ui/NotificationTypesFilter/NotificationTypesFilter.constants'
import {
  checkboxItemAll,
  checkboxItemUnread
} from '@/components/shared/checkbox/lib/constants'

import { NotificationsService } from '..'
import {
  NOTIFICATIONS_COUNT_RQ_KEY,
  useNotificationsCount
} from './useNotificationsCount'
import { NOTIFICATIONS_LIST_RQ_KEY } from './useNotificationsList'
import { NOTIFICATIONS_LIST_INFINITE_QUERY_KEY } from './useNotificationsListInfinite'

type CountRequestReturnType = Awaited<
  ReturnType<typeof NotificationsService.getNotificationsCount>
>

type Props = {
  typeAlias?: string[]
}

export const useSetNotificationReadAll = ({ typeAlias }: Props) => {
  const queryClient = useQueryClient()
  const { refetch } = useNotificationsCount()
  const { selectedProjectId } = useSelectedProjectStore()

  return useMutation({
    mutationFn: async () => {
      await NotificationsService.setNotificationReadAll({
        projectId: selectedProjectId,
        typeAlias: (!typeAlias ||
        (typeAlias.length === 1 && typeAlias[0] === checkboxItemUnread)
          ? notificationTypeOptionsMap.flatMap(typeAlias =>
              Array.isArray(typeAlias.value)
                ? typeAlias.value
                : [typeAlias.value]
            )
          : typeAlias
        ).filter(
          alias => alias !== checkboxItemUnread && alias !== checkboxItemAll
        )
      }).then(() => {
        refetch()
      })
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [NOTIFICATIONS_LIST_RQ_KEY, 1]
      })
      await queryClient.cancelQueries({
        queryKey: [NOTIFICATIONS_LIST_INFINITE_QUERY_KEY]
      })

      const previousNotificationData = queryClient.getQueryData([
        NOTIFICATIONS_LIST_RQ_KEY,
        1
      ])
      const previousInfiniteNotificationData = queryClient.getQueryData([
        NOTIFICATIONS_LIST_INFINITE_QUERY_KEY
      ])

      // Для случаев, если сработал инфинити скролл
      queryClient.setQueryData<
        InfiniteData<{ page: number; response: ListRequestReturnType }>
      >([NOTIFICATIONS_LIST_INFINITE_QUERY_KEY], old =>
        notificationHelpersByAll.updateInfiniteReadStatus(old)
      )

      // // Для случаев, без инфинити скролла
      queryClient.setQueryData<ListRequestReturnType>(
        [NOTIFICATIONS_LIST_RQ_KEY, 1],
        old => notificationHelpersByAll.updateFirstPageReadStatus(old)
      )

      queryClient.setQueryData<CountRequestReturnType>(
        [NOTIFICATIONS_COUNT_RQ_KEY],
        old => {
          if (!old) {
            return old
          }

          return {
            ...old,
            data: {
              ...old.data,
              data: {
                count: 0
              }
            }
          }
        }
      )

      return {
        previousNotificationList: previousNotificationData,
        previousInfiniteNotificationList: previousInfiniteNotificationData
      }
    },
    onError: (err, newTodo, context) => {
      queryClient.setQueryData(
        [NOTIFICATIONS_LIST_INFINITE_QUERY_KEY],
        context?.previousInfiniteNotificationList
      )
      queryClient.setQueryData(
        [NOTIFICATIONS_LIST_RQ_KEY, 1],
        context?.previousNotificationList
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATIONS_LIST_INFINITE_QUERY_KEY]
      })
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATIONS_LIST_RQ_KEY, 1]
      })
    }
  })
}
